import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Clock, Star, Instagram, Phone } from 'lucide-react';

// 4 Slideshow Images (for gallery)
const SLIDESHOW_IMAGES = [
  { id: 'gallery-1', url: "https://customer-assets-v7afamib.emergentagent.net/job_shamrock-preview/artifacts/j332ijx0_WhatsApp%20Image%202026-07-12%20at%201.19.31%20PM%20%283%29.jpeg" },
  { id: 'gallery-2', url: "https://customer-assets-v7afamib.emergentagent.net/job_shamrock-preview/artifacts/fh5yxnnh_Outdoors.jpeg" },
  { id: 'gallery-3', url: "https://customer-assets-v7afamib.emergentagent.net/job_shamrock-preview/artifacts/5lg4h0bc_Chips%26Beers.jpeg" },
  { id: 'gallery-4', url: "https://customer-assets-v7afamib.emergentagent.net/job_shamrock-preview/artifacts/ukwbpm4g_WhatsApp%20Image%202026-07-12%20at%201.19.30%20PM%20%283%29.jpeg" },
];

// Hero slide backgrounds
const HERO_SLIDES = [
  {
    id: 'slide-shamrock',
    type: 'shamrock',
    background: "https://customer-assets-v7afamib.emergentagent.net/job_shamrock-preview/artifacts/2zj1qgud_ChatGPT%20Image%20Jul%2031%2C%202026%2C%2002_47_22%20PM.png",
  },
  {
    id: 'slide-gruezi',
    type: 'gruezi',
    background: "https://images.unsplash.com/photo-1709010454595-1e8328c9f3c3?w=1920&auto=format&fit=crop",
  },
  {
    id: 'slide-outdoor',
    type: 'gruezi_bern',
    background: "https://customer-assets-v7afamib.emergentagent.net/job_shamrock-preview/artifacts/fh5yxnnh_Outdoors.jpeg",
  },
];

const HERO_SLIDE_COUNT = HERO_SLIDES.length;
const GALLERY_IMAGE_COUNT = SLIDESHOW_IMAGES.length;

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auto-rotate hero slides
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev === HERO_SLIDE_COUNT - 1 ? 0 : prev + 1));
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  // Auto-rotate gallery images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === GALLERY_IMAGE_COUNT - 1 ? 0 : prevIndex + 1
      );
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div data-testid="home-page" className="min-h-screen bg-[#1a1a1a]">
      {/* Hero Section with Alternating Slides */}
      <section 
        data-testid="hero-section"
        className="relative h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Slide 1: The Shamrock */}
        <div className={`absolute inset-0 transition-opacity duration-1000 ${currentSlide === 0 ? 'opacity-100' : 'opacity-0'}`}>
          <img
            src={HERO_SLIDES[0].background}
            alt="The Shamrock Bar"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1a1a1a]/40" />
          
          {/* Content for Slide 1 - Minimal overlay to let Guinness image shine */}
          <div className="absolute inset-0 flex items-end justify-center z-10 pb-32">
            <div className="text-center px-4 max-w-4xl mx-auto">
              {/* Tagline */}
              <p className="text-[#E8E6D9] tracking-[0.4em] uppercase text-sm md:text-base mb-4 font-['Manrope'] font-medium">
                Your Irish Pub in Bern
              </p>
              
              {/* Decorative Line */}
              <div className="flex items-center justify-center mb-6">
                <div className="w-12 h-px bg-[#D4AF37]/50"></div>
                <span className="mx-4 text-[#D4AF37] text-xl">☘</span>
                <div className="w-12 h-px bg-[#D4AF37]/50"></div>
              </div>
              
              {/* Subtitle */}
              <p className="text-[#E8E6D9]/80 font-['Manrope'] text-base md:text-lg mb-8">
                Live Sports • Traditional Irish Food & Drinks • Great Atmosphere
              </p>
              
              {/* Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/menu"
                  className="min-w-[160px] bg-[#D4AF37] text-[#1a1a1a] px-10 py-4 font-['Manrope'] font-semibold text-sm tracking-[0.15em] hover:bg-[#E5C568] transition-all duration-300"
                >
                  MENU
                </Link>
                <Link
                  to="/location"
                  className="min-w-[160px] border border-[#E8E6D9]/50 text-[#E8E6D9] px-10 py-4 font-['Manrope'] font-medium text-sm tracking-[0.15em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
                >
                  LOCATION
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Slide 2: Grüezi, Bern! */}
        <div className={`absolute inset-0 transition-opacity duration-1000 ${currentSlide === 1 ? 'opacity-100' : 'opacity-0'}`}>
          <img
            src={HERO_SLIDES[1].background}
            alt="Guinness"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1a1a1a]/50" />
          
          {/* Content for Slide 2 - Grüezi Bern */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4 max-w-4xl mx-auto">
              {/* Shamrock Icon */}
              <div className="flex justify-center mb-6">
                <span className="text-[#D4AF37] text-5xl">☘</span>
              </div>
              
              {/* Title */}
              <h1 className="font-['Playfair_Display'] text-5xl md:text-7xl lg:text-8xl font-normal text-white mb-8 tracking-wide">
                GRÜEZI, BERN!
              </h1>
              
              {/* Opening Hours Label */}
              <div className="flex items-center justify-center mb-6">
                <div className="w-12 h-px bg-[#D4AF37]"></div>
                <span className="mx-4 text-[#D4AF37] tracking-[0.3em] uppercase text-sm font-['Manrope']">
                  Opening Hours
                </span>
                <div className="w-12 h-px bg-[#D4AF37]"></div>
              </div>
              
              {/* Opening Hours */}
              <div className="space-y-2 text-white font-['Manrope'] text-base md:text-lg">
                <div className="flex justify-center items-center gap-4">
                  <span className="w-32 text-right">MON - THU</span>
                  <span className="text-[#D4AF37]">|</span>
                  <span className="w-32 text-left">11:00 - 23:00</span>
                </div>
                <div className="flex justify-center items-center gap-4">
                  <span className="w-32 text-right">FRIDAY</span>
                  <span className="text-[#D4AF37]">|</span>
                  <span className="w-32 text-left">11:00 - 01:00</span>
                </div>
                <div className="flex justify-center items-center gap-4">
                  <span className="w-32 text-right">SATURDAY</span>
                  <span className="text-[#D4AF37]">|</span>
                  <span className="w-32 text-left">16:00 - 01:00</span>
                </div>
                <div className="flex justify-center items-center gap-4">
                  <span className="w-32 text-right">SUNDAY</span>
                  <span className="text-[#D4AF37]">|</span>
                  <span className="w-32 text-left">16:00 - 23:00</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Slide 3: Grüezi Bärn with Beer Mug */}
        <div className={`absolute inset-0 transition-opacity duration-1000 ${currentSlide === 2 ? 'opacity-100' : 'opacity-0'}`}>
          <img
            src={HERO_SLIDES[2].background}
            alt="Grüezi Bärn"
            className="w-full h-full object-cover"
          />
          {/* Minimal overlay to preserve the image design */}
          <div className="absolute inset-0 bg-[#1a1a1a]/20" />
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-20">
          {HERO_SLIDES.map((slide, index) => (
            <button
              key={slide.id}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-[#D4AF37] w-8' 
                  : 'bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* About Section */}
      <section 
        data-testid="about-section"
        className="py-24 md:py-32 bg-[#1a1a1a]"
      >
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-6 font-['Manrope']">
            Welcome
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl font-normal text-[#E8E6D9] mb-8 leading-tight">
            A Modern Chapter in<br />Irish Tradition
          </h2>
          <p className="text-[#A3A3A3] font-['Manrope'] text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            <span className="bg-[#D4AF37] text-[#1a1a1a] px-1">Life&apos;s too short for boring pubs!</span> At The Shamrock in Bern, we serve hearty food, legendary Guinness, live sports, pub quizzes, karaoke, and music. Come for one drink, stay for the craic—and don&apos;t blame us if you become the karaoke champion! 🍻
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-[#141414]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="text-center p-8">
              <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37]/30 rounded-full flex items-center justify-center">
                <span className="text-2xl">🍺</span>
              </div>
              <h3 className="font-['Playfair_Display'] text-xl text-[#E8E6D9] mb-3">
                Premium Drinks
              </h3>
              <p className="text-[#A3A3A3] font-['Manrope'] text-sm leading-relaxed">
                Guinness on tap, Irish whiskeys, craft beers, and signature cocktails.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="text-center p-8">
              <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37]/30 rounded-full flex items-center justify-center">
                <span className="text-2xl">📺</span>
              </div>
              <h3 className="font-['Playfair_Display'] text-xl text-[#E8E6D9] mb-3">
                Live Sports
              </h3>
              <p className="text-[#A3A3A3] font-['Manrope'] text-sm leading-relaxed">
                Multiple screens showing football, rugby, GAA, and all major sporting events.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="text-center p-8">
              <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37]/30 rounded-full flex items-center justify-center">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="font-['Playfair_Display'] text-xl text-[#E8E6D9] mb-3">
                Games & Fun
              </h3>
              <p className="text-[#A3A3A3] font-['Manrope'] text-sm leading-relaxed">
                Darts, foosball, quiz nights, and live music for the ultimate pub experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-4 font-['Manrope']">
              Gallery
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-normal text-[#E8E6D9]">
              The Experience
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {SLIDESHOW_IMAGES.map((image) => (
              <div key={image.id} className="aspect-square overflow-hidden group">
                <img 
                  src={image.url} 
                  alt={`Gallery ${image.id}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location & Hours */}
      <section className="py-24 bg-[#141414]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Hours */}
            <div className="text-center md:text-left">
              <h3 className="font-['Playfair_Display'] text-2xl text-[#D4AF37] mb-6">
                Opening Hours
              </h3>
              <div className="space-y-2 text-[#E8E6D9] font-['Manrope']">
                <div className="flex justify-between md:justify-start md:gap-8">
                  <span className="text-[#A3A3A3]">Mon - Thu</span>
                  <span>11:00 - 23:00</span>
                </div>
                <div className="flex justify-between md:justify-start md:gap-8">
                  <span className="text-[#A3A3A3]">Friday</span>
                  <span>11:00 - 01:00</span>
                </div>
                <div className="flex justify-between md:justify-start md:gap-8">
                  <span className="text-[#A3A3A3]">Saturday</span>
                  <span>16:00 - 01:00</span>
                </div>
                <div className="flex justify-between md:justify-start md:gap-8">
                  <span className="text-[#A3A3A3]">Sunday</span>
                  <span>16:00 - 23:00</span>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="text-center md:text-left">
              <h3 className="font-['Playfair_Display'] text-2xl text-[#D4AF37] mb-6">
                Find Us
              </h3>
              <div className="space-y-3 text-[#E8E6D9] font-['Manrope']">
                <p>Schwarztorstrasse 11</p>
                <p>3007 Bern, Switzerland</p>
                <p className="pt-2">
                  <a href="tel:+41337227000" className="hover:text-[#D4AF37] transition-colors">
                    033 722 70 00
                  </a>
                </p>
                <p>
                  <a href="mailto:bern@theshamrock.ch" className="hover:text-[#D4AF37] transition-colors">
                    bern@theshamrock.ch
                  </a>
                </p>
              </div>
              <div className="flex justify-center md:justify-start gap-4 mt-6">
                <a 
                  href="https://instagram.com/theshamrockbern" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors text-[#E8E6D9]"
                >
                  <Instagram size={18} />
                </a>
                <a 
                  href="https://g.page/r/CQCYgCwqyZXzEBM/review" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors text-[#E8E6D9]"
                >
                  <Star size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1a1a1a] border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-normal text-[#E8E6D9] mb-6">
            See You at <span className="text-[#D4AF37]">The Shamrock</span>
          </h2>
          <p className="text-[#A3A3A3] font-['Manrope'] mb-8">
            Come for the drinks, stay for the craic.
          </p>
          <Link
            to="/reservations"
            className="inline-block border border-[#D4AF37] text-[#D4AF37] px-12 py-4 font-['Manrope'] font-medium text-sm tracking-[0.15em] hover:bg-[#D4AF37] hover:text-[#1a1a1a] transition-all duration-300"
          >
            RESERVE A TABLE
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
